import React, { useEffect, useState, useContext, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate,
} from "react-router";
import { unauthenticatedRoutes, authenticatedRoutes } from './Routes'
import { signOut, fetchAuthSession } from "@aws-amplify/auth";
import Loader from "../components/loaders/Loader";
import Sidebar from "../components/layouts/Sidebar";
import Topbar from "../components/layouts/Topbar";
import GlobalContext from "../components/Context/GlobalContext";
import NotFound from "../components/Notfound/Notfound";
import { setPrivilegedArray } from "../store/slices/privilegeSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { setUserDetails, setUserEmail } from "../store/slices/userEmail";
import useApi from "../services/useApi";
import { setLanguage } from "../store/slices/languageSlice";

const AppRoutes: React.FC = () => {
  const { isLoggedIn, setIsLoggedIn } = useContext(GlobalContext);
  const [isLoading, setIsLoading] = useState(true); // State to track loading
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { privileges } = useSelector((state: RootState) => state.privileges)
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAPI } = useApi();

  // Check if privileges are still loading (empty array means not loaded yet)
  const isPrivilegesLoading = isLoggedIn && (privileges === undefined || privileges === null || (Array.isArray(privileges) && privileges.length === 0));

  const checkAuth = async () => {
    try {
      // Default language set to English and disabled language change option as per client request
      dispatch(setLanguage("english"));
      const user = await fetchAuthSession({ forceRefresh: true });
      if (user.tokens) {
        // Safely parse privileges
        const privileges = user?.tokens?.idToken?.payload?.['custom:privileges'];
        const email = user?.tokens?.idToken?.payload?.email as string || '';
        dispatch(setUserEmail(email));

        // Fetch user details from API
        try {
          const response = await getAPI(`admins/email/${email}`);
          if (!response.error && response.data) {
            dispatch(setUserDetails(response.data));
          }
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
        if (!privileges) {
          dispatch(setPrivilegedArray('superadmin'));
        } else {
          let privilegesArray: string[] = [];

          if (Array.isArray(privileges)) {
            privilegesArray = privileges as string[];
          } else if (typeof privileges === 'string') {
            privilegesArray = privileges.split(','); // Split by comma if privileges are a single string
          }
          dispatch(setPrivilegedArray(privilegesArray));
        }
        setIsLoggedIn(true);
        // Navigate to dashboard or default authenticated route
      } else {
        if (
          location.pathname !== "/forgot-password" &&
          location.pathname !== "/set-new-password" &&
          location.pathname !== "/password-changed"
        ) {
          setIsLoggedIn(false);
          navigate("/");
          localStorage.clear();
          signOut();
        }
      }

    } catch (error) {

      if (
        location.pathname !== "/forgot-password" &&
        location.pathname !== "/set-new-password" &&
        location.pathname !== "/password-changed"
      ) {
        setIsLoggedIn(false);
        navigate("/");
        localStorage.clear();
        signOut();
      }
    } finally {
      setTimeout(() => { //added the timeout to complete the loading animations
        setIsLoading(false); // Stop loading after 2 seconds
      }, 2000);
    }
  };

  useEffect(() => {
    checkAuth(); // Call checkAuth on initial render
  }, []);

  // Show Loader while checking auth
  if (isLoading) {
    return <Loader />;
  }

  // Function to render routes dynamically with privilege checks
  const renderRoutes = (

    routes: typeof unauthenticatedRoutes | typeof authenticatedRoutes
  ) =>
    routes.map(({ path, element: Component, props, privilege }) => {
      // Check if the user has the required privilege
      if (privileges === 'superadmin' && privilege === 'supper-admin') {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }
      if (privileges === 'superadmin' && privilege === 'admin-allowed') {
        return null;
      }
      if (privileges !== 'superadmin' && privilege === 'admin-allowed') {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }

      if (privileges === 'superadmin') {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }

      if (privilege === 'allowed') {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }
      if (Array.isArray(privilege) && privilege.some(privilege => privileges.includes(privilege))) {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }
      if (privilege && !privileges.includes(privilege)) {
        return null; // Do not render the route if the user lacks the privilege
      }
      return <Route key={path} path={path} element={<Component {...props} />} />;
    });

  // React Router matches "/dashboard/" to the "/dashboard" route, so the
  // trailing slash is stripped here too — otherwise the exact comparisons
  // below miss and the sidebar/topbar are hidden on an otherwise valid URL.
  const currentPath = location.pathname.replace(/\/+$/, "") || "/";

  // Check if the current path matches any of the valid paths and verify privileges
  const showLayout = authenticatedRoutes.some((route) => {
    // Handle dynamic routes with regex
    const { path, privilege } = route;

    if (path.includes(":")) {
      const regexPath = path.replace(/:\w+/g, "[^/]+");
      const regex = new RegExp(`^${regexPath}$`);
      if (regex.test(currentPath)) {
        // Check privilege for matching route
        return checkPrivilege(privilege);
      }
    } else if (path === currentPath) {
      // Check privilege for matching route
      return checkPrivilege(privilege);
    }

    return false;
  });
  function checkPrivilege(privilege: any) {
    if (privileges === 'superadmin' && privilege === 'supper-admin') {
      return true;
    }
    if (privileges !== 'superadmin' && privilege === 'admin-allowed') {
      return true;
    }
    if (privileges === 'superadmin' && privilege === 'admin-allowed') {
      return false;
    }
    if (privileges === 'superadmin') {
      return true;
    }
    if (privilege === 'allowed') {
      return true;
    }
    if (Array.isArray(privilege)) {
      return privilege.some((p) => privileges.includes(p));
    } else {
      if (typeof privilege === "string") {
        return privileges.includes(privilege);
      }
    }
  }
  // Show full-screen loader while privileges are loading
  if (isPrivilegesLoading) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen">
      {/* Show Sidebar and Topbar only when authenticated */}
      {!isLoggedIn ? (
        <Suspense fallback={<Loader />}>
          <Routes>
            {renderRoutes(unauthenticatedRoutes)}
            {/* <Route path="*" element={<NotFoundPage />} /> */}
          </Routes>
        </Suspense>
      ) : (
        <>
          {currentPath !== "/not-found" ? (
            <div className="flex w-full">
              {showLayout && (
                <>
                  {/* Mobile backdrop */}
                  <div
                    className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${isMobileSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                    onClick={() => setIsMobileSidebarOpen(false)}
                  />
                  {/* Sidebar: fixed overlay on mobile, static on desktop */}
                  <div className={`fixed md:static inset-y-0 left-0 z-50 w-[300px] flex-shrink-0 transition-transform duration-300 md:translate-x-0 ${isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
                    <Sidebar onClose={() => setIsMobileSidebarOpen(false)} />
                  </div>
                </>
              )}
              <div className="flex-1 flex flex-col min-w-0 bg-white">
                {showLayout && <Topbar onMenuClick={() => setIsMobileSidebarOpen(true)} />}
                <div className="flex-1 bg-background_blue rounded-[8px] p-3 h-mainscreenheight overflow-y-auto">
                  <Suspense fallback={<Loader />}>
                    <Routes>
                      {renderRoutes(authenticatedRoutes)}
                      <Route path="/" element={<Navigate to="/" />} />
                    </Routes>
                  </Suspense>
                </div>
              </div>
            </div>
          ) :
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Catch-all route */}
                <Route path="/not-found" element={<NotFound />} />
              </Routes>
            </Suspense>
          }
        </>
      )}
    </div>
  );
};

const App: React.FC = () => (
  <Router>
    <AppRoutes />
  </Router>
);

export default App;